import { lazy, Suspense, useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MoreVertical, Check } from "lucide-react";
import { catalogThumbnailUrl } from "@/lib/avatarThumbnails";
import { useLanguage } from "@/i18n/LanguageProvider";
import type { UserAvatar } from "@/hooks/useAvatarLibrary";

const AvatarCard = lazy(() => import("@/components/AvatarCard"));

interface AvatarLibraryCardProps {
  avatar: UserAvatar;
  isLastAvatar: boolean;
  onSetActive: () => void;
  onRename: (newLabel: string) => void;
  onDelete: () => void;
  // Optional — only provided for editable MetaPerson-generated rows when the
  // creation flow is available (Premium + METAPERSON_ENABLED).
  onEdit?: () => void;
}

type MenuMode = "none" | "menu" | "rename" | "confirmDelete";

const AvatarLibraryCard = ({
  avatar,
  isLastAvatar,
  onSetActive,
  onRename,
  onDelete,
  onEdit,
}: AvatarLibraryCardProps) => {
  const { t } = useLanguage();
  const [mode, setMode] = useState<MenuMode>("none");
  const [renameValue, setRenameValue] = useState(avatar.label || "");
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (mode !== "menu") return;
    const onDocDown = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setMode("none");
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
  }, [mode]);

  // Post-W4 Avaturn deprecation: all live rows are 'metaperson'. The
  // ternary keeps a defensive 'MetaPerson' label for any legacy row that
  // somehow surfaces — Avaturn label retired.
  const providerLabel = avatar.provider === "metaperson" ? "MetaPerson" : "MetaPerson";
  const isAvaturn = false;
  // Catalog previews already have companion images. A library of thumbnails
  // must not create a WebGL context and decode a full model for every card.
  const posterUrl = avatar.avatar_url.includes('/prerendered/')
    ? avatar.avatar_url.replace(/\.glb(?=\?|$)/i, '.png') : null;

  const handleCardTap = () => {
    if (mode !== "none") return;
    if (!avatar.is_active) onSetActive();
  };

  const submitRename = () => {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== (avatar.label || "")) {
      onRename(trimmed);
    }
    setMode("none");
  };

  return (
    <div ref={containerRef} className="relative">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        onClick={handleCardTap}
        className="vyv-card overflow-hidden rounded-2xl relative"
        style={{
          border: avatar.is_active
            ? "1px solid hsl(187 72% 50% / 0.5)"
            : "1px solid hsl(222 18% 20% / 0.6)",
          boxShadow: avatar.is_active
            ? "0 0 24px hsl(187 72% 50% / 0.15)"
            : "none",
          cursor: avatar.is_active ? "default" : "pointer",
        }}
      >
        {/* Active badge */}
        {avatar.is_active && (
          <div
            className="absolute top-2 left-2 z-20 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-display font-semibold tracking-wide"
            style={{
              background: "hsl(187 72% 50% / 0.18)",
              color: "hsl(187 72% 75%)",
              border: "1px solid hsl(187 72% 50% / 0.35)",
            }}
          >
            <span
              className="w-1.5 h-1.5 rounded-full"
              style={{ background: "hsl(187 72% 65%)" }}
            />
            {t("settings.avatars.active")}
          </div>
        )}

        {/* Menu button */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setMode(mode === "menu" ? "none" : "menu");
          }}
          className="absolute top-2 right-2 z-20 w-7 h-7 rounded-full flex items-center justify-center transition-colors"
          style={{
            background: "hsl(222 35% 9% / 0.85)",
            border: "1px solid hsl(222 18% 25% / 0.6)",
          }}
          aria-label="menu"
        >
          <MoreVertical size={12} className="text-foreground/70" />
        </button>

        {/* Thumbnail area */}
        <div className="w-full" style={{ background: "hsl(222 30% 9%)" }}>
          {posterUrl ? (
            <img src={catalogThumbnailUrl(avatar.avatar_url) ?? posterUrl} alt="" loading="lazy" decoding="async"
              onError={(event) => {
                if (event.currentTarget.src !== posterUrl) event.currentTarget.src = posterUrl;
              }}
              className="w-full h-[160px] object-contain" width={160} height={160} />
          ) : (
            <Suspense fallback={<div className="h-[160px]" />}>
              <AvatarCard modelUrl={avatar.avatar_url} isAvaturnModel={isAvaturn} height="160px" />
            </Suspense>
          )}
        </div>

        {/* Footer */}
        <div className="px-3 py-2.5 flex flex-col gap-1.5" style={{ background: "hsl(222 30% 11%)" }}>
          <p className="text-[12px] font-display font-semibold text-foreground truncate">
            {avatar.label || t("settings.avatars.unnamed")}
          </p>
          <span
            className="self-start text-[9px] font-display tracking-wide px-1.5 py-0.5 rounded-full"
            style={{
              background: "hsl(222 18% 20% / 0.6)",
              color: "hsl(210 20% 75%)",
              border: "1px solid hsl(222 18% 28% / 0.6)",
            }}
          >
            {providerLabel}
          </span>
        </div>
      </motion.div>

      {/* Dropdown menu */}
      <AnimatePresence>
        {mode === "menu" && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
            className="absolute top-10 right-2 z-30 w-40 rounded-xl overflow-hidden"
            style={{
              background: "hsl(222 35% 12%)",
              border: "1px solid hsl(222 18% 22% / 0.8)",
              boxShadow: "0 8px 24px hsl(222 40% 4% / 0.5)",
            }}
          >
            {!avatar.is_active && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setMode("none");
                  onSetActive();
                }}
                className="w-full text-left px-3 py-2.5 text-[12px] font-display text-foreground/85 hover:bg-foreground/[0.04] transition-colors"
              >
                {t("settings.avatars.menu.activate")}
              </button>
            )}
            {onEdit && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setMode("none");
                  onEdit();
                }}
                className="w-full text-left px-3 py-2.5 text-[12px] font-display text-foreground/85 hover:bg-foreground/[0.04] transition-colors"
              >
                {t("metaperson.settings.editFace")}
              </button>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setRenameValue(avatar.label || "");
                setMode("rename");
              }}
              className="w-full text-left px-3 py-2.5 text-[12px] font-display text-foreground/85 hover:bg-foreground/[0.04] transition-colors"
            >
              {t("settings.avatars.menu.rename")}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setMode("confirmDelete");
              }}
              className="w-full text-left px-3 py-2.5 text-[12px] font-display hover:bg-foreground/[0.04] transition-colors"
              style={{ color: "hsl(0 70% 68%)" }}
            >
              {t("settings.avatars.menu.delete")}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rename modal */}
      <AnimatePresence>
        {mode === "rename" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-6"
            style={{ background: "hsl(222 40% 4% / 0.7)" }}
            onClick={() => setMode("none")}
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm rounded-2xl p-5"
              style={{
                background: "hsl(222 35% 10%)",
                border: "1px solid hsl(222 18% 20% / 0.8)",
              }}
            >
              <p className="text-[13px] font-display font-semibold text-foreground mb-3">
                {t("settings.avatars.menu.rename")}
              </p>
              <input
                autoFocus
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submitRename();
                  if (e.key === "Escape") setMode("none");
                }}
                placeholder={t("settings.avatars.renamePlaceholder")}
                className="w-full bg-transparent px-3 py-2.5 rounded-xl text-[14px] text-foreground outline-none placeholder:text-foreground/30"
                style={{
                  background: "hsl(222 30% 11%)",
                  border: "1px solid hsl(222 18% 20% / 0.6)",
                }}
              />
              <div className="flex gap-2 mt-4">
                <button
                  type="button"
                  onClick={() => setMode("none")}
                  className="flex-1 rounded-xl py-2.5 font-display text-[12px]"
                  style={{
                    background: "hsl(222 18% 18% / 0.6)",
                    color: "hsl(210 20% 85%)",
                    border: "1px solid hsl(222 18% 30% / 0.6)",
                  }}
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="button"
                  onClick={submitRename}
                  className="flex-1 rounded-xl py-2.5 font-display font-semibold text-[12px]"
                  style={{
                    background: "hsl(187 72% 50%)",
                    color: "hsl(222 38% 6%)",
                  }}
                >
                  {t("common.save")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete confirm modal */}
      <AnimatePresence>
        {mode === "confirmDelete" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-6"
            style={{ background: "hsl(222 40% 4% / 0.7)" }}
            onClick={() => setMode("none")}
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.94, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm rounded-2xl p-5"
              style={{
                background: "hsl(222 35% 10%)",
                border: "1px solid hsl(222 18% 20% / 0.8)",
              }}
            >
              <p className="text-[13px] font-display font-semibold text-foreground mb-2">
                {t("settings.avatars.deleteConfirm")}
              </p>
              {isLastAvatar && (
                <p className="text-[11px] text-foreground/60 font-body mb-3">
                  {t("settings.avatars.deleteLastWarning")}
                </p>
              )}
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  onClick={() => setMode("none")}
                  className="flex-1 rounded-xl py-2.5 font-display text-[12px]"
                  style={{
                    background: "hsl(222 18% 18% / 0.6)",
                    color: "hsl(210 20% 85%)",
                    border: "1px solid hsl(222 18% 30% / 0.6)",
                  }}
                >
                  {t("common.cancel")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setMode("none");
                    onDelete();
                  }}
                  className="flex-1 rounded-xl py-2.5 font-display font-semibold text-[12px] inline-flex items-center justify-center gap-1.5"
                  style={{
                    background: "hsl(0 70% 55%)",
                    color: "hsl(0 0% 100%)",
                  }}
                >
                  <Check size={12} />
                  {t("settings.avatars.deleteConfirmYes")}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AvatarLibraryCard;
